/**
 * ganttFillResolver.ts
 * Phase 4, Item 2 — Pure function: resolve the Highcharts fill spec for a Gantt bar.
 *
 * Rules:
 *   - Envelope rows (project / workOrder / machinesGroup / staffGroup):
 *     always solid, never hatched (they are thin header-band rows).
 *   - machineBooking / employeeAssignment with status = 'Committed':
 *     solid fill at full opacity.
 *   - machineBooking / employeeAssignment with status = 'Tentative':
 *     diagonal-stripe Highcharts SVG pattern at 60% opacity.
 *   - Maintenance or Calibration event type → maintenance (amber) colour.
 *   - OutOfService event type              → outOfService (grey-brown) colour.
 *
 * Highcharts pattern fill reference:
 *   https://api.highcharts.com/highcharts/series.gantt.data.color
 *   A point's `color` may be set to a PatternObject:
 *     {
 *       pattern: {
 *         path: { d: 'M 0 0 L 10 10 M -2 8 L 8 -2 M 2 10 L 10 2', strokeWidth: 2 },
 *         color: '#RRGGBB',
 *         backgroundColor: '#RRGGBB',
 *         width: 10,
 *         height: 10,
 *         opacity: 0.6,
 *       }
 *     }
 *
 * The resolver returns either:
 *   - a plain string (hex colour) for solid fills, OR
 *   - a HighchartsPatternFill object for hatched fills.
 *
 * This module has zero side-effects and can be unit-tested without a browser.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** The subset of theme.schedule.* tokens this resolver needs. */
export interface FillTokens {
  committed: string;
  tentative: string;
  machineTime: string;
  employeeAlloc: string;
  maintenance: string;
  outOfService: string;
  projectEnvelope: string;
  workOrderEnvelope: string;
  /**
   * Run Mode Phase C — continuous (24/7) machine bookings. Distinct deep
   * sage-teal fill so a bar that legitimately runs through weekends is
   * visually its own species (legend: "Continuous (runs 24/7)").
   */
  continuous: string;
}

/**
 * Narrowed task-type discriminator used by GanttPoint.
 *
 * BUG-2026-0194 adds two summary (envelope) rows for the drill-down levels:
 *   machineSummary — one machine on a work order, or one test type on that
 *                    machine ("RK-SAW · 4 tasks · 6 days"); machine-time fill
 *   personSummary  — one person on a work order ("S. Hegger · 6 tasks · 10%");
 *                    employee-allocation fill
 * Both are header rows (never navigable, never status-hatched) but drawn a
 * little stronger than the top-level Machines/Staff group bands so the
 * "total work" envelope reads as the row's content.
 */
export type GanttTaskType =
  | 'project'
  | 'workOrder'
  | 'machinesGroup'
  | 'staffGroup'
  | 'machineSummary'
  | 'personSummary'
  | 'machineBooking'
  | 'employeeAssignment';

/** Booking / assignment status — null for group/envelope rows. */
export type GanttStatus = 'Tentative' | 'Committed' | null;

/** Event type that overrides normal colour logic. */
export type GanttEventType = 'Maintenance' | 'Calibration' | 'OutOfService' | null;

/**
 * Highcharts pattern object shape.
 * Typed conservatively — Highcharts accepts this at runtime even if
 * TypeScript's @types/highcharts doesn't fully enumerate pattern options.
 */
export interface HighchartsPatternFill {
  pattern: {
    path: {
      d: string;
      strokeWidth: number;
    };
    /** Stripe line colour (same as the bar colour). */
    color: string;
    /** Background fill of the pattern tile. */
    backgroundColor: string;
    /** Width / height of the repeating pattern tile in pixels. */
    width: number;
    height: number;
    opacity: number;
  };
}

/** Union: either a hex string or a Highcharts pattern object. */
export type GanttFill = string | HighchartsPatternFill;

// ---------------------------------------------------------------------------
// Diagonal-stripe pattern constructor
// ---------------------------------------------------------------------------

/**
 * Build a Highcharts pattern fill for Tentative bars.
 *
 * Pattern: diagonal stripes (45°) — 2px stripe on a 10×10 tile,
 * using `color` for the stripe and a faded version of the same
 * colour (35% opacity) as the background.
 *
 * The returned object is passed directly to a Highcharts point's `color` field.
 */
function tentativePattern(color: string): HighchartsPatternFill {
  return {
    pattern: {
      // SVG path draws two diagonal lines per 10×10 tile, giving a
      // continuous-looking stripe across the bar.
      path: {
        d: 'M 0 10 L 10 0 M -2 2 L 2 -2 M 8 12 L 12 8',
        strokeWidth: 2,
      },
      color,                   // stripe line colour
      backgroundColor: color,  // Highcharts blends this with opacity
      width: 10,
      height: 10,
      opacity: 0.6,
    },
  };
}

// ---------------------------------------------------------------------------
// Base colour resolution (before applying commit/tentative modifier)
// ---------------------------------------------------------------------------

function baseColor(
  taskType: GanttTaskType,
  eventType: GanttEventType,
  tokens: FillTokens,
  runMode?: string | null,
): string {
  // Event-type overrides apply to booking/assignment rows only.
  if (taskType === 'machineBooking' || taskType === 'employeeAssignment') {
    if (eventType === 'Maintenance' || eventType === 'Calibration') {
      return tokens.maintenance;
    }
    if (eventType === 'OutOfService') {
      return tokens.outOfService;
    }
  }

  // Run Mode Phase C: a continuous (24/7) machine booking gets the distinct
  // continuous token — Committed stays solid, Tentative stays hatched (the
  // stripe/pattern modifier below is orthogonal to the base colour).
  if (taskType === 'machineBooking' && runMode === 'continuous') {
    return tokens.continuous;
  }

  switch (taskType) {
    case 'project':
      return tokens.projectEnvelope;
    case 'workOrder':
      return tokens.workOrderEnvelope;
    case 'machinesGroup':
    case 'machineSummary':
      return tokens.machineTime;
    case 'staffGroup':
    case 'personSummary':
      return tokens.employeeAlloc;
    case 'machineBooking':
      return tokens.machineTime;
    case 'employeeAssignment':
      return tokens.employeeAlloc;
    default:
      // Should never reach here — TypeScript exhaustiveness guard
      return tokens.committed;
  }
}

// ---------------------------------------------------------------------------
// Main export
// ---------------------------------------------------------------------------

/**
 * Resolve the Highcharts `color` property for a single Gantt point.
 *
 * @param taskType   Row hierarchy type from GanttPoint.taskType
 * @param status     'Tentative' | 'Committed' | null (null = envelope/group row)
 * @param eventType  Override for maintenance/calibration/out-of-service events
 * @param tokens     Theme color tokens (from theme.schedule.*)
 * @param runMode    Run Mode Phase C — 'continuous' machine bookings take the
 *                   continuous token as their base colour. Omit/null = attended.
 *
 * @returns  A hex string for solid fills or a HighchartsPatternFill for hatched fills.
 */
export function resolvePointFill(
  taskType: GanttTaskType,
  status: GanttStatus,
  eventType: GanttEventType,
  tokens: FillTokens,
  runMode?: string | null,
): GanttFill {
  const color = baseColor(taskType, eventType, tokens, runMode);

  // Envelope / group rows are always solid (they act as category headers,
  // not as status-driven booking bars).
  const isEnvelopeRow =
    taskType === 'project' ||
    taskType === 'workOrder' ||
    taskType === 'machinesGroup' ||
    taskType === 'staffGroup' ||
    taskType === 'machineSummary' ||
    taskType === 'personSummary';

  if (isEnvelopeRow || status === 'Committed' || status === null) {
    return color;
  }

  // Tentative → diagonal-stripe pattern
  return tentativePattern(color);
}
